import { useCallback, useEffect, useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError } from "@pontimed/api-client";
import { createFileRoute } from "@tanstack/react-router";
import { Ban, CheckCircle, Clock, DollarSign, Users, Wallet } from "lucide-react";

import { AdminCountryPills } from "@/components/admin/country-pills";
import {
  availablePayoutActions,
  PayoutActionDialog,
  type PayoutAction,
  type PayoutActionTarget,
} from "@/components/admin/referrals/payout-action-dialog";
import {
  WalletDetailModal,
  type WalletDetailTarget,
} from "@/components/admin/referrals/wallet-detail-modal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminCountries } from "@/hooks/use-admin-countries";
import { useAdminFetch } from "@/hooks/use-admin-fetch";
import { showToast } from "@/lib/ui/toast";

/**
 * Admin → Referrals & Wallet management.
 *
 * Port of `src/app/admin/referrals/page.tsx` (legacy NextJS, ~1100 LOC) per D21.
 *
 * V1.0 scope (declared upfront per D21 § exception): 5 tabs ported (overview /
 * referrers / commissions / payouts / tax) with view + core actions:
 *   - Approve all pending commissions
 *   - Block / unblock referral partner
 *   - Tax CSV export
 *
 * V1.1 deferred (additive — same endpoints, no schema change required):
 *   - Wallet detail modal (`/api/admin/referrals/wallet?userId=`)
 *   - Manual wallet adjustment modal + reason
 *   - Payout actions: cancel (with reason) + mark-paid (with reference)
 *   - Tax filing reference entry → mark-tax-filed batch
 *
 * Endpoints (live per T1 inventory):
 *   - GET  /api/admin/referrals?tab=&country=
 *   - POST /api/admin/referrals/commissions/approve
 *   - POST /api/admin/referrals/block
 *   - GET  /api/admin/referrals/tax-report?year=&quarter=&country=
 */

interface OverviewData {
  totalReferrals: number;
  activeReferrals: number;
  activeReferrers: number;
  pendingCommissions: { count: number; total: number };
  totalPayouts: { count: number; total: number };
  walletBalances: { usersWithBalance: number; totalBalance: number };
}

interface Commission {
  id: string;
  period: string;
  year: number;
  subscriptionAmount: number;
  pct: number;
  amount: number;
  currency: string;
  status: string;
  referrer: { id: string; name: string };
  referred: { name: string; type: string; plan: string | null };
}

interface Payout {
  id: string;
  type: string;
  amount: number;
  currency: string;
  invoice: {
    number: string | null;
    url: string | null;
    date: string | null;
    businessName: string | null;
    vatNumber: string | null;
  };
  payment: { method: string | null; reference: string | null; paidAt: string | null };
  status: string;
  adminNotes: string | null;
  createdAt: string;
  user: { id: string; name: string; iban: string | null; swift: string | null };
}

interface Referrer {
  id: string;
  name: string;
  code: string | null;
  balance: number;
  currency: string;
  terms: Record<string, unknown> | null;
  activatedAt: string | null;
  totalReferrals: number;
  pendingCommissions: number;
  enabled: boolean;
}

interface TaxReportSummary {
  year: number;
  quarter: number | string;
  country: string;
  periodFrom: string;
  periodTo: string;
  totalPayouts: number;
  totalGrossAmount: number;
  totalWithholdingTax: number;
  totalNetPaid: number;
  byPartner: Array<{
    userId: string;
    name: string;
    iban: string | null;
    fiscalType: string | null;
    payoutCount: number;
    grossTotal: number;
    withholdingTotal: number;
    netTotal: number;
    taxFiled: boolean;
  }>;
}

type Tab = "overview" | "referrers" | "commissions" | "payouts" | "tax";

// Brand tokens, not raw Tailwind palette classes: the `*-soft` families
// already carry their own dark-mode value, so no `dark:` pair is needed and
// the colours stay in step with the rest of the product (CLAUDE.md, "Design
// Tokens — No Hardcoded Colors").
const statusColor: Record<string, string> = {
  pending: "bg-warning-soft text-warning-strong",
  approved: "bg-info-soft text-info-strong",
  paid: "bg-success-soft text-success-strong",
  cancelled: "bg-destructive-soft text-destructive-strong",
  pending_invoice: "bg-warning-soft text-warning-strong",
  invoice_received: "bg-info-soft text-info-strong",
  processing: "bg-accent-soft text-accent-strong",
};

export const Route = createFileRoute("/_authenticated/referrals")({
  component: ReferralsPage,
});

function ReferralsPage() {
  const { t } = useLingui();
  const { adminFetch } = useAdminFetch();
  const { visibleCountries: countries } = useAdminCountries();
  const [selectedCountry, setSelectedCountry] = useState("");
  const [activeTab, setActiveTab] = useState<Tab>("overview");

  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [commissionTotals, setCommissionTotals] = useState({ count: 0, amount: 0 });
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [referrers, setReferrers] = useState<Referrer[]>([]);
  const [loading, setLoading] = useState(true);

  const [taxSummary, setTaxSummary] = useState<TaxReportSummary | null>(null);
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [taxQuarter, setTaxQuarter] = useState<number | null>(null);

  const [walletTarget, setWalletTarget] = useState<WalletDetailTarget | null>(null);
  const [payoutTarget, setPayoutTarget] = useState<PayoutActionTarget | null>(null);
  const [exportingCsv, setExportingCsv] = useState(false);

  const countryCounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const c of countries) {
      if (c.totalProviders > 0) map[c.code] = c.totalProviders;
    }
    return map;
  }, [countries]);

  const fetchTab = useCallback(
    async (tab: Tab) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ tab });
        if (selectedCountry) params.set("country", selectedCountry);
        const data = await adminFetch.get<Record<string, unknown>>(
          `/api/admin/referrals?${params}`,
        );
        switch (tab) {
          case "overview":
            setOverview(data as unknown as OverviewData);
            break;
          case "commissions":
            setCommissions((data.commissions as Commission[]) || []);
            setCommissionTotals(
              (data.pendingTotal as { count: number; amount: number }) || { count: 0, amount: 0 },
            );
            break;
          case "payouts":
            setPayouts((data.payouts as Payout[]) || []);
            break;
          case "referrers":
            setReferrers((data.referrers as Referrer[]) || []);
            break;
          case "tax":
            break;
        }
      } catch {
        /* ignore */
      }
      setLoading(false);
    },
    [adminFetch, selectedCountry],
  );

  useEffect(() => {
    fetchTab(activeTab);
  }, [activeTab, fetchTab]);

  const fetchTaxReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: taxYear.toString(), country: "BG" });
      if (taxQuarter) params.set("quarter", taxQuarter.toString());
      const data = await adminFetch.get<{ summary: TaxReportSummary | null }>(
        `/api/admin/referrals/tax-report?${params}`,
      );
      setTaxSummary(data.summary || null);
    } catch {
      /* ignore */
    }
    setLoading(false);
  }, [adminFetch, taxYear, taxQuarter]);

  useEffect(() => {
    if (activeTab === "tax") fetchTaxReport();
  }, [activeTab, fetchTaxReport]);

  const approveAllPending = async () => {
    try {
      await adminFetch.post("/api/admin/referrals/commissions/approve", {
        commissionIds: commissions.filter((c) => c.status === "pending").map((c) => c.id),
      });
      fetchTab("commissions");
      fetchTab("overview");
    } catch {
      /* ignore */
    }
  };

  const blockReferral = async (userId: string, currentlyEnabled: boolean) => {
    try {
      await adminFetch.post("/api/admin/referrals/block", {
        userId,
        blocked: currentlyEnabled,
        reason: "Admin action",
      });
      fetchTab("referrers");
    } catch (e) {
      if (e instanceof ApiError && e.status === 501) {
        // Backend stub — surface inline. V1.1 wallet module pending.
        showToast(t`Backend stub — block/unblock will land in V1.1`, "info");
      }
    }
  };

  // The CSV is served by the admin API on api.pontimed.app and is scoped by the
  // `X-Admin-DB` header the admin fetch interceptor injects. `window.open` on a
  // relative path sends no headers and, from admin.pontimed.app, would open the
  // SPA shell instead of the file — so stream it through adminFetch and save the
  // blob.
  const exportTaxCsv = async () => {
    const params = new URLSearchParams({
      year: taxYear.toString(),
      country: "BG",
      format: "csv",
    });
    if (taxQuarter) params.set("quarter", taxQuarter.toString());
    setExportingCsv(true);
    const toast = showToast(t`Preparing CSV…`, "loading");
    try {
      const res = await adminFetch.raw(`/api/admin/referrals/tax-report?${params}`, {
        method: "GET",
      });
      if (!res.ok) throw new ApiError(res.status, null);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `withholding-tax-BG-${taxYear}${taxQuarter ? `-Q${taxQuarter}` : ""}.csv`;
      // Attach before clicking: a detached anchor is ignored by some engines.
      // Revoke on the next tick — revoking synchronously can cancel a download
      // the browser has only just started.
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      toast.update(t`CSV downloaded`, "success");
    } catch {
      toast.update(t`CSV export failed`, "error");
    } finally {
      setExportingCsv(false);
    }
  };

  const fmt = (n: number) => `€${n.toFixed(2)}`;

  const tabs: { key: Tab; label: React.ReactNode }[] = [
    { key: "overview", label: <Trans>Overview</Trans> },
    { key: "referrers", label: <Trans>Referrers</Trans> },
    { key: "commissions", label: <Trans>Commissions</Trans> },
    { key: "payouts", label: <Trans>Payouts</Trans> },
    { key: "tax", label: <Trans>Tax Report</Trans> },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          <Trans>Referral &amp; Wallet Management</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>Commission approval, payout processing, and wallet management</Trans>
        </p>
      </div>

      <AdminCountryPills
        countries={countries.map((c) => ({ code: c.code, name: c.name, flag: c.flag }))}
        counts={countryCounts}
        selected={selectedCountry}
        onSelect={setSelectedCountry}
      />

      <div className="flex gap-1 overflow-x-auto rounded-lg border p-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`min-h-[44px] flex-1 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.key
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
        </div>
      ) : (
        <>
          {activeTab === "overview" && overview && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
                {[
                  {
                    label: <Trans>Total Referrals</Trans>,
                    value: overview.totalReferrals,
                    icon: Users,
                    color: "text-blue-600",
                  },
                  {
                    label: <Trans>Active</Trans>,
                    value: overview.activeReferrals,
                    icon: CheckCircle,
                    color: "text-green-600",
                  },
                  {
                    label: <Trans>Referrers</Trans>,
                    value: overview.activeReferrers,
                    icon: Users,
                    color: "text-indigo-600",
                  },
                  {
                    label: <Trans>Pending Commissions</Trans>,
                    value: `${overview.pendingCommissions.count} (${fmt(overview.pendingCommissions.total)})`,
                    icon: Clock,
                    color: "text-amber-600",
                  },
                  {
                    label: <Trans>Total Paid</Trans>,
                    value: fmt(overview.totalPayouts.total),
                    icon: DollarSign,
                    color: "text-purple-600",
                  },
                  {
                    label: <Trans>Wallet Balance</Trans>,
                    value: `${overview.walletBalances.usersWithBalance} (${fmt(overview.walletBalances.totalBalance)})`,
                    icon: Wallet,
                    color: "text-teal-600",
                  },
                ].map((stat, i) => {
                  const Icon = stat.icon;
                  return (
                    <div key={i} className="bg-card rounded-xl border p-4">
                      <div className="mb-1 flex items-center gap-2">
                        <Icon className={`size-4 ${stat.color}`} />
                        <p className="text-muted-foreground text-xs">{stat.label}</p>
                      </div>
                      <p className={`text-lg font-bold ${stat.color}`}>{stat.value}</p>
                    </div>
                  );
                })}
              </div>

              <div className="bg-card flex flex-wrap gap-3 rounded-xl border p-4">
                <Button size="sm" onClick={() => setActiveTab("commissions")}>
                  <Clock className="mr-2 size-4" />
                  <Trans>Review pending ({overview.pendingCommissions.count})</Trans>
                </Button>
                <Button size="sm" variant="outline" onClick={() => setActiveTab("payouts")}>
                  <DollarSign className="mr-2 size-4" />
                  <Trans>Payout queue</Trans>
                </Button>
              </div>
            </div>
          )}

          {activeTab === "referrers" && (
            <div className="bg-card rounded-xl border">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 border-b">
                      <th className="px-3 py-2 text-left">
                        <Trans>User</Trans>
                      </th>
                      <th className="hidden px-3 py-2 text-left sm:table-cell">
                        <Trans>Code</Trans>
                      </th>
                      <th className="hidden px-3 py-2 text-left md:table-cell">
                        <Trans>Offer</Trans>
                      </th>
                      <th className="hidden px-3 py-2 text-right sm:table-cell">
                        <Trans>Referrals</Trans>
                      </th>
                      <th className="px-3 py-2 text-right">
                        <Trans>Balance</Trans>
                      </th>
                      <th className="px-3 py-2 text-right">
                        <Trans>Actions</Trans>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-muted-foreground px-3 py-8 text-center">
                          <Trans>No active referrers yet</Trans>
                        </td>
                      </tr>
                    ) : (
                      referrers.map((r) => {
                        const terms = r.terms as Record<string, number> | null;
                        const openWallet = () =>
                          setWalletTarget({ userId: r.id, name: r.name, enabled: r.enabled });
                        return (
                          <tr
                            key={r.id}
                            className="hover:bg-muted/30 cursor-pointer border-b"
                            onClick={openWallet}
                          >
                            <td className="px-3 py-2">
                              <div className="font-medium">{r.name}</div>
                              {r.pendingCommissions > 0 && (
                                <Badge variant="outline" className="mt-0.5 text-[0.625rem]">
                                  <Trans>{r.pendingCommissions} pending</Trans>
                                </Badge>
                              )}
                            </td>
                            <td className="text-muted-foreground hidden px-3 py-2 font-mono text-xs sm:table-cell">
                              {r.code || "—"}
                            </td>
                            <td className="hidden px-3 py-2 text-xs md:table-cell">
                              {terms
                                ? `${terms.commission_y1}/${terms.commission_y2}/${terms.commission_y3}%`
                                : "—"}
                            </td>
                            <td className="hidden px-3 py-2 text-right sm:table-cell">
                              {r.totalReferrals}
                            </td>
                            <td className="px-3 py-2 text-right font-medium">{fmt(r.balance)}</td>
                            <td className="px-3 py-2 text-right">
                              <div
                                className="flex items-center justify-end gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  onClick={openWallet}
                                  className="hover:bg-muted min-h-[36px] min-w-[36px] rounded-md p-1.5"
                                  aria-label={t`View wallet`}
                                  title={t`View wallet`}
                                >
                                  <Wallet className="size-4" />
                                </button>
                                <button
                                  onClick={() => blockReferral(r.id, r.enabled)}
                                  className="hover:bg-muted min-h-[36px] min-w-[36px] rounded-md p-1.5"
                                  aria-label={r.enabled ? t`Block referral` : t`Unblock referral`}
                                  title={r.enabled ? t`Block referral` : t`Unblock referral`}
                                >
                                  <Ban className={`size-4 ${!r.enabled ? "text-red-500" : ""}`} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "commissions" && (
            <div className="space-y-4">
              {commissionTotals.count > 0 && (
                <div className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/20">
                  <div>
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                      <Trans>
                        {commissionTotals.count} pending commissions —{" "}
                        {fmt(commissionTotals.amount)}
                      </Trans>
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      <Trans>Review and approve to credit referrer wallets</Trans>
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={approveAllPending}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="mr-2 size-4" />
                    <Trans>Approve All</Trans>
                  </Button>
                </div>
              )}

              <div className="bg-card rounded-xl border">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        <th className="px-3 py-2 text-left">
                          <Trans>Period</Trans>
                        </th>
                        <th className="px-3 py-2 text-left">
                          <Trans>Referrer</Trans>
                        </th>
                        <th className="hidden px-3 py-2 text-left sm:table-cell">
                          <Trans>Referred</Trans>
                        </th>
                        <th className="hidden px-3 py-2 text-right md:table-cell">
                          <Trans>Subscription</Trans>
                        </th>
                        <th className="hidden px-3 py-2 text-right sm:table-cell">
                          <Trans>Rate</Trans>
                        </th>
                        <th className="px-3 py-2 text-right">
                          <Trans>Commission</Trans>
                        </th>
                        <th className="px-3 py-2 text-center">
                          <Trans>Status</Trans>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-muted-foreground px-3 py-8 text-center">
                            <Trans>No commissions yet</Trans>
                          </td>
                        </tr>
                      ) : (
                        commissions.map((c) => (
                          <tr key={c.id} className="hover:bg-muted/30 border-b">
                            <td className="px-3 py-2">
                              <div className="font-medium">{c.period}</div>
                              <div className="text-muted-foreground text-xs">
                                <Trans>Year {c.year}</Trans>
                              </div>
                            </td>
                            <td className="px-3 py-2">{c.referrer.name}</td>
                            <td className="hidden px-3 py-2 sm:table-cell">
                              <div>{c.referred.name}</div>
                              <div className="text-muted-foreground text-xs">
                                {c.referred.type} · {c.referred.plan || "—"}
                              </div>
                            </td>
                            <td className="hidden px-3 py-2 text-right md:table-cell">
                              {fmt(c.subscriptionAmount)}
                            </td>
                            <td className="hidden px-3 py-2 text-right sm:table-cell">{c.pct}%</td>
                            <td className="px-3 py-2 text-right font-medium">{fmt(c.amount)}</td>
                            <td className="px-3 py-2 text-center">
                              <Badge className={statusColor[c.status] || ""}>{c.status}</Badge>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === "payouts" && (
            <div className="bg-card rounded-xl border">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 border-b">
                      <th className="px-3 py-2 text-left">
                        <Trans>Date</Trans>
                      </th>
                      <th className="px-3 py-2 text-left">
                        <Trans>Partner</Trans>
                      </th>
                      <th className="hidden px-3 py-2 text-left md:table-cell">
                        <Trans>Type</Trans>
                      </th>
                      <th className="hidden px-3 py-2 text-left sm:table-cell">
                        <Trans>Invoice</Trans>
                      </th>
                      <th className="px-3 py-2 text-right">
                        <Trans>Amount</Trans>
                      </th>
                      <th className="px-3 py-2 text-center">
                        <Trans>Status</Trans>
                      </th>
                      <th className="px-3 py-2 text-right">
                        <Trans>Actions</Trans>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-muted-foreground px-3 py-8 text-center">
                          <Trans>No payouts yet</Trans>
                        </td>
                      </tr>
                    ) : (
                      payouts.map((p) => (
                        <tr key={p.id} className="hover:bg-muted/30 border-b">
                          <td className="text-muted-foreground px-3 py-2 text-xs">
                            {new Date(p.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-medium">{p.user.name}</div>
                            {p.user.iban && (
                              <div className="text-muted-foreground font-mono text-[0.6875rem]">
                                {p.user.iban}
                              </div>
                            )}
                          </td>
                          <td className="hidden px-3 py-2 md:table-cell">
                            <Badge variant="outline" className="text-[0.625rem]">
                              {p.type}
                            </Badge>
                          </td>
                          <td className="hidden px-3 py-2 sm:table-cell">
                            {p.invoice.number ? (
                              p.invoice.url ? (
                                <a
                                  href={p.invoice.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary text-xs underline"
                                >
                                  {p.invoice.number}
                                </a>
                              ) : (
                                <span className="text-xs">{p.invoice.number}</span>
                              )
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {fmt(p.amount)} {p.currency}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <Badge className={statusColor[p.status] || ""}>{p.status}</Badge>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap justify-end gap-1">
                              {availablePayoutActions(p.status).map((action) => (
                                <Button
                                  key={action}
                                  size="sm"
                                  variant={action === "cancel" ? "outline" : "default"}
                                  className="min-h-[2.75rem]"
                                  onClick={() =>
                                    setPayoutTarget({
                                      id: p.id,
                                      action,
                                      partnerName: p.user.name,
                                      amount: p.amount,
                                      currency: p.currency,
                                      invoiceNumber: p.invoice.number,
                                      invoiceUrl: p.invoice.url,
                                    })
                                  }
                                >
                                  <PayoutActionLabel action={action} />
                                </Button>
                              ))}
                              {availablePayoutActions(p.status).length === 0 && (
                                <span className="text-muted-foreground text-xs">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "tax" && (
            <div className="space-y-4">
              <div className="bg-card flex flex-wrap items-end gap-3 rounded-xl border p-4">
                <div>
                  <label className="mb-1 block text-xs font-medium">
                    <Trans>Year</Trans>
                  </label>
                  <input
                    type="number"
                    value={taxYear}
                    onChange={(e) => setTaxYear(Number(e.target.value))}
                    className="border-border bg-background rounded-md border px-3 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium">
                    <Trans>Quarter (optional)</Trans>
                  </label>
                  <select
                    value={taxQuarter ?? ""}
                    onChange={(e) => setTaxQuarter(e.target.value ? Number(e.target.value) : null)}
                    className="border-border bg-background rounded-md border px-3 py-1.5 text-sm"
                  >
                    <option value="">{t`Full year`}</option>
                    <option value="1">Q1</option>
                    <option value="2">Q2</option>
                    <option value="3">Q3</option>
                    <option value="4">Q4</option>
                  </select>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={exportingCsv}
                  onClick={() => void exportTaxCsv()}
                >
                  {exportingCsv ? <Trans>Exporting…</Trans> : <Trans>Export CSV</Trans>}
                </Button>
              </div>

              {taxSummary && (
                <div className="bg-card rounded-xl border p-4">
                  <h3 className="mb-3 text-sm font-semibold">
                    <Trans>
                      {taxSummary.country} · {String(taxSummary.quarter)}/{taxSummary.year}
                    </Trans>
                  </h3>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Stat label={t`Payouts`} value={taxSummary.totalPayouts} />
                    <Stat label={t`Gross`} value={fmt(taxSummary.totalGrossAmount)} />
                    <Stat label={t`Withholding`} value={fmt(taxSummary.totalWithholdingTax)} />
                    <Stat label={t`Net paid`} value={fmt(taxSummary.totalNetPaid)} />
                  </div>

                  {taxSummary.byPartner.length > 0 && (
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-muted/50 border-b">
                            <th className="px-3 py-2 text-left">
                              <Trans>Partner</Trans>
                            </th>
                            <th className="hidden px-3 py-2 text-left sm:table-cell">IBAN</th>
                            <th className="px-3 py-2 text-right">
                              <Trans>Payouts</Trans>
                            </th>
                            <th className="px-3 py-2 text-right">
                              <Trans>Gross</Trans>
                            </th>
                            <th className="px-3 py-2 text-right">
                              <Trans>Net</Trans>
                            </th>
                            <th className="px-3 py-2 text-center">
                              <Trans>Tax filed</Trans>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {taxSummary.byPartner.map((p) => (
                            <tr key={p.userId} className="hover:bg-muted/30 border-b">
                              <td className="px-3 py-2 font-medium">{p.name}</td>
                              <td className="text-muted-foreground hidden px-3 py-2 font-mono text-xs sm:table-cell">
                                {p.iban || "—"}
                              </td>
                              <td className="px-3 py-2 text-right">{p.payoutCount}</td>
                              <td className="px-3 py-2 text-right">{fmt(p.grossTotal)}</td>
                              <td className="px-3 py-2 text-right font-medium">
                                {fmt(p.netTotal)}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {p.taxFiled ? (
                                  <Badge className="bg-green-100 text-green-700">
                                    <Trans>Filed</Trans>
                                  </Badge>
                                ) : (
                                  <Badge variant="outline">
                                    <Trans>Pending</Trans>
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <WalletDetailModal
        target={walletTarget}
        onClose={() => setWalletTarget(null)}
        onChanged={() => {
          fetchTab("referrers");
          fetchTab("overview");
        }}
      />

      <PayoutActionDialog
        // Remount per (payout, action) so typed reference/reason never leak
        // from one payout's dialog into the next — see the component header.
        key={payoutTarget ? `${payoutTarget.id}:${payoutTarget.action}` : "none"}
        target={payoutTarget}
        onClose={() => setPayoutTarget(null)}
        post={(body) => adminFetch.post("/api/admin/referrals/payouts", body)}
        onDone={() => {
          // Both tabs move: the payout row changes status, and the overview
          // counters (pending payouts, amounts) are computed from the same
          // rows.
          fetchTab("payouts");
          fetchTab("overview");
        }}
      />
    </div>
  );
}

/**
 * Button label per action. A component, not a string helper: the Lingui macro
 * only rewrites `t` where it can see the `useLingui()` binding, so passing `t`
 * into a plain function would leave the strings unextracted. The row reads as
 * a verb ("Approve"); the dialog states the full sentence.
 */
function PayoutActionLabel({ action }: { action: PayoutAction }) {
  if (action === "approve") return <Trans>Approve</Trans>;
  if (action === "process") return <Trans>Mark paid</Trans>;
  return <Trans>Cancel</Trans>;
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="text-muted-foreground text-[0.625rem] font-semibold tracking-wide uppercase">
        {label}
      </div>
      <div className="mt-1 text-lg font-bold">{value}</div>
    </div>
  );
}
